import { useEffect, useState } from 'react';
import { Spinner, VStack } from '@chakra-ui/react';
import { useNavigate } from '@tanstack/react-router';
import { AuthLayout } from '@/components/authentication/ui/AuthLayout';
import { useAuth } from '@/context/AuthContext';
import { useInviteTokenInfo } from '@/hooks/access/useInviteTokenInfo';
import { propertyNavigateOptions, Routes } from '@/routes';
import MagicTokenRedemption from '@/components/access/MagicTokenRedemption';
import DirectMagicRedemption from '@/components/access/DirectMagicRedemption';
import PropertyAccessLanding from '@/components/access/PropertyAccessLanding';
import AccountMismatch from '@/components/access/AccountMismatch';
import AccessExpired from '@/components/access/AccessExpired';
import { accessLogger } from '@/utils/access/accessLogger';

export const Route = createFileRoute({
	validateSearch: (search: Record<string, unknown>) => ({
		token: (search.token as string) || '',
		property: (search.property as string) || undefined,
	}),
	component: RedeemPage,
});

function RedeemPage() {
	const { token, property } = Route.useSearch();
	const { currentUser, isUserLoading } = useAuth();
	const navigate = useNavigate();
	const [showGuestFlow, setShowGuestFlow] = useState(false);

	const { data: tokenInfo, isLoading: isTokenLoading } = useInviteTokenInfo(token);

	const propertyPublicId = property || tokenInfo?.property_public_id || null;

	const [isRedirecting, setIsRedirecting] = useState(false);

	const onRedeemed = () => {
		setIsRedirecting(true);
		if (propertyPublicId) {
			navigate(propertyNavigateOptions(propertyPublicId));
		} else {
			navigate({ to: Routes.PROPERTIES });
		}
	};

	// Auto-navigate when logged-in user already has property access (Scenario A / C)
	useEffect(() => {
		if (!currentUser || !tokenInfo || tokenInfo.status === 'expired') return;
		if (tokenInfo.has_property_access && propertyPublicId) {
			accessLogger.info(
				`User already has access to ${propertyPublicId}, navigating directly (token not redeemed)`,
			);
			navigate(propertyNavigateOptions(propertyPublicId));
		}
	}, [currentUser, tokenInfo, propertyPublicId, navigate]);

	if (isTokenLoading || isUserLoading) {
		return (
			<AuthLayout title="">
				<VStack gap={6} textAlign="center">
					<Spinner size="xl" />
				</VStack>
			</AuthLayout>
		);
	}

	if (!tokenInfo || tokenInfo.status === 'expired') {
		accessLogger.info('Token expired or not found, showing AccessExpired screen');
		return <AccessExpired propertyAddress={tokenInfo?.property_address} />;
	}

	// Show a spinner while navigation is completing after redemption. Without this,
	// a query refetch triggered by the new guest token causes a re-render that creates
	// a fresh DirectMagicRedemption instance (ref reset) and fires a second redeem call.
	if (isRedirecting) {
		return (
			<AuthLayout title="">
				<VStack gap={6} textAlign="center">
					<Spinner size="xl" />
				</VStack>
			</AuthLayout>
		);
	}

	// Guest flow: once the user has elected to continue without login, stay in that flow
	// regardless of auth-state changes that happen while the redemption is in flight.
	if (showGuestFlow) {
		if (tokenInfo.requires_otp === false) {
			accessLogger.info('not logged in, QR grant — skipping OTP, redeeming directly');
			return <DirectMagicRedemption magicToken={token} onRedeemed={onRedeemed} />;
		}
		accessLogger.info('not logged in, showing guest OTP flow');
		return <MagicTokenRedemption magicToken={token} property={property} onRedeemed={onRedeemed} />;
	}

	// Not logged in (Scenario E)
	if (!currentUser) {
		accessLogger.info('not logged in, showing landing page');
		// TODO: People shared to as Admin must complete fully credentialed account
		// registration, so hide "Continue without a login" for that role. The backend
		// magic-token info response does not currently expose the invited property role,
		// so the frontend cannot reliably apply this Admin-only requirement.
		return (
			<PropertyAccessLanding
				token={token}
				property={property}
				onContinueWithoutLogin={() => setShowGuestFlow(true)}
			/>
		);
	}

	// Logged-in: email mismatch + no access → show mismatch screen (Scenario D)
	if (tokenInfo.email_matches === false && !tokenInfo.has_property_access) {
		accessLogger.info('email mismatch + no access, showing AccountMismatch screen');
		return <AccountMismatch token={token} property={property} />;
	}

	// Logged-in: has access → auto-navigate handled by useEffect above, show spinner meanwhile (Scenario A/C)
	if (tokenInfo.has_property_access) {
		return (
			<AuthLayout title="">
				<VStack gap={6} textAlign="center">
					<Spinner size="xl" />
				</VStack>
			</AuthLayout>
		);
	}

	// Logged-in: email matches + no access → OTP flow to grant access (Scenario B)
	// Not reached by the current sharing flow (access is granted at invite time, so
	// has_property_access is already true when the user clicks the link). Reserved for
	// future flows that issue a magic link without pre-granting a house role.
	accessLogger.info('email matches, no access — showing OTP grant flow');
	return <MagicTokenRedemption magicToken={token} property={property} onRedeemed={onRedeemed} />;
}
